import { gql, useApolloClient } from '@apollo/client';
import {
  ArticleFormat,
  GetSuggestedTagsListQuery,
  GetSuggestedTagsListQueryVariables,
} from '../../__generated__/queries-web';
import {
  getTagTotalArticleUsage,
  hasUntrackedArticleUsage,
} from '../../utils/getTagTotalArticleUsage';

const GET_SUGGESTED_TAGS_QUERY = gql`
  query GetSuggestedTagsList(
    $article: ArticleLikeInput!
    $where: ArticleTagsTagFilterSearchInput
    $brandKey: BrandKey
  ) {
    suggestTags(article: $article, where: $where) {
      id
      title
      type
      relationId
      articleUsages(brandKeys: [$brandKey]) {
        total
        brandKey
      }
      brandedContents(where: { brandKey: $brandKey }) {
        isActive
        brandKey
      }
    }
  }
`;

interface SearchSuggestedTagsParams {
  article: GetSuggestedTagsListQueryVariables['article'];
  where?: GetSuggestedTagsListQueryVariables['where'];
  brandKey?: GetSuggestedTagsListQueryVariables['brandKey'];
}

export const useSearchSuggestedTagsGetterPromise = () => {
  const client = useApolloClient();

  return ({ article, where, brandKey }: SearchSuggestedTagsParams) => {
    if (!brandKey) {
      return Promise.resolve({ data: { suggestTags: [] } } as any);
    }

    return client
      .query<GetSuggestedTagsListQuery, GetSuggestedTagsListQueryVariables>({
        query: GET_SUGGESTED_TAGS_QUERY,
        variables: {
          article:
            article?.format === ArticleFormat.Video
              ? { title: article.title }
              : article,
          where,
          brandKey,
        },
      })
      .then((result) => {
        if (!result.data?.suggestTags) {
          return result;
        }

        return {
          ...result,
          data: {
            ...result.data,
            suggestTags: result.data.suggestTags.filter(
              (tag) => getTagTotalArticleUsage(tag.articleUsages) > 0,
            ),
          },
        };
      });
  };
};

export const useSearchSuggestedTagsGetter = () => {
  const client = useApolloClient();

  return ({ article, where, brandKey }: GetSuggestedTagsListQueryVariables) => {
    if (!brandKey) {
      return Promise.resolve([]);
    }

    return client
      .query<GetSuggestedTagsListQuery, GetSuggestedTagsListQueryVariables>({
        query: GET_SUGGESTED_TAGS_QUERY,
        variables: {
          article:
            article?.format === ArticleFormat.Video
              ? { title: article.title }
              : article,
          where,
          brandKey,
        },
      })
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw errors[0];
        }

        return (data?.suggestTags ?? []).filter(
          (tag) =>
            hasUntrackedArticleUsage(tag.type) ||
            getTagTotalArticleUsage(tag.articleUsages) > 0,
        );
      });
  };
};
